import React, { useEffect, useRef, useState } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";
import AdminSpinner from "../../components/Spinner/Spinner";

interface EurAIProofModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdf: Blob | null;
  row: any;
  onAccept: () => void;
  onDecline: () => void;
}

export default function EurAIProofModal({
  isOpen,
  row,
  onClose,
  pdf,
  onAccept,
  onDecline,
}: EurAIProofModalProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pdf) {
      const url = URL.createObjectURL(pdf);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [pdf]);

  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    function escape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (isOpen) {
      document.addEventListener("mousedown", clickOutside);
      document.addEventListener("keydown", escape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("mousedown", clickOutside);
      document.removeEventListener("keydown", escape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/accept_eurai_proof`, {
        code: row.code,
      });
      onAccept();
      onClose();
    } catch (e) {
      console.error("Error accepting EurAI proof:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/decline_eurai_proof`, {
        code: row.code,
      });
      onDecline();
      onClose();
    } catch (e) {
      console.error("Error declining EurAI proof:", e);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[80vh] flex flex-col p-6 relative"
        role="dialog"
        aria-modal="true"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {isLoading ? (
          <div>
            <AdminSpinner />
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h2 className="text-xl font-bold">EurAI Proof Validation</h2>
              <p className="text-gray-600">
                Reviewing membership proof for{" "}
                <strong>{row?.name}</strong> ({row?.email}).
              </p>
            </div>

            <div className="flex-1 min-h-0 mb-4 border border-gray-300 rounded-md overflow-hidden">
              {pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0`}
                  className="w-full h-full"
                  title="EurAI proof"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  No document available
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button
                onClick={handleDecline}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                <XCircle className="h-4 w-4" />
                Decline
              </button>
              <button
                onClick={handleAccept}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4" />
                Accept
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
